import type { GenericProps } from 'bz-core-controls/controls/form/field/generics/GenerateComponentType';
import { GenerateComponentType } from 'bz-core-controls/controls/form/field/generics/GenerateComponentType';
import Checkbox from 'click-controls/controls/form/field/field-checkbox';
import type { ICheckboxProps } from 'click-controls/controls/form/field/field-checkbox';
import type { IProps } from './form/field/field-select';
import FieldSelect from './form/field/field-select';

import type { ComponentType } from 'react';
import Field from './form/field';
import DatePicker from './form/field/field-date-picker';
import FieldTimePicker from './form/field/field-time-picker';
import type { IFieldTimePickerProps } from './form/field/field-time-picker';

import type { IBaseFieldProps } from 'bz-core-controls/controls/form/field/generics/IBaseFieldProps';
import Switch from 'click-controls/controls/form/field/field-switch';
import type { ISwitchProps } from 'click-controls/controls/form/field/field-switch';
import NumberField from './form/field/field-integer';

import TextArea from './form/field/field-textarea';
import type { ITextAreaAriaProps } from './form/field/field-textarea';

import { bind as bound } from 'bind-decorator';
import type { IProps as FieldAutoSuggestProps } from './form/field/field-autosuggest';
import FieldAutoSuggest from './form/field/field-autosuggest';
import FieldDOB from './form/field/field-date-of-birth';
import type { IFieldDOBProps } from './form/field/field-date-of-birth';

import LookupFieldSelect from './form/field/field-lookup-select';
import type { ILookupFieldSelectProps } from './form/field/field-lookup-select';

import type { IFieldProps } from 'bz-core-controls/controls/form/field/field/IFieldProps';
import FieldCurrency from 'click-controls/controls/form/field/field-currency';
import type { IFieldCurrencyProps } from 'click-controls/controls/form/field/field-currency';
import type { IDatePickerAriaProps } from './form/field/field-date-picker-react-aria';
import type { INestedLookupFieldSelectProps } from './form/field/field-nested-lookup-select';
import NestedLookupFieldSelect from './form/field/field-nested-lookup-select';
const components: { [key: string]: any } = {};

export class Fields<T> {
  getCached<P>(
    key: string,
    RawComponent: ComponentType<P>,
    onChangeMethodName: string = 'onChange'
  ): ComponentType<Omit<P & GenericProps<T>, keyof IBaseFieldProps>> {
    let res = components[key] as ComponentType<Omit<P & GenericProps<T>, keyof IBaseFieldProps>>;
    if (!res) {
      components[key] = GenerateComponentType<T, P>(RawComponent, onChangeMethodName);
      res = components[key] as ComponentType<Omit<P & GenericProps<T>, keyof IBaseFieldProps>>;
    }
    return res;
  }

  get Field() {
    return this.getCached<IFieldProps>('Field', Field);
  }

  get FieldSelect() {
    return this.getCached<IProps>('FieldSelect', FieldSelect);
  }

  get FieldSwitch() {
    return this.getCached<ISwitchProps>('FieldSwitch', Switch);
  }

  get FieldDOB() {
    return this.getCached<IFieldDOBProps>('FieldDOB', FieldDOB);
  }

  get DatePicker() {
    return this.getCached<IDatePickerAriaProps>('DatePicker', DatePicker);
  }

  get FieldTimePicker() {
    return this.getCached<IFieldTimePickerProps>('FieldTimePicker', FieldTimePicker);
  }

  get NumberField() {
    return this.getCached<IFieldProps>('NumberField', NumberField);
  }

  get TextArea() {
    return this.getCached<ITextAreaAriaProps>('TextArea', TextArea);
  }
  get FieldAutoSuggest() {
    return this.getCached<FieldAutoSuggestProps>('FieldAutoSuggest', FieldAutoSuggest);
  }
  get LookupFieldSelect() {
    return this.getCached<ILookupFieldSelectProps>('LookupFieldSelect', LookupFieldSelect);
  }

  get NestedLookupFieldSelect() {
    return this.getCached<INestedLookupFieldSelectProps>(
      'NestedLookupFieldSelect',
      NestedLookupFieldSelect
    );
  }

  get Checkbox() {
    return this.getCached<ICheckboxProps>('Checkbox', Checkbox);
  }

  get FieldCurrency() {
    return this.getCached<IFieldCurrencyProps>('FieldCurrency', FieldCurrency);
  }

  @bound
  create<P>(
    key: string,
    component: ComponentType<P>
  ): ComponentType<Omit<P & GenericProps<T>, keyof IBaseFieldProps>> {
    return this.getCached<P>(key, component);
  }
}
